<template>
  <div ref="root" class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center gap-3">
      <span class="text-sm font-semibold text-gray-900">Line items</span>
      <span class="text-xs text-gray-500">Tick what ships on this date and how many.</span>
      <span class="ml-auto flex w-full flex-wrap items-center gap-3 sm:w-auto">
        <input
          v-if="lines.length > LINE_FILTER_MIN"
          v-model="query"
          data-no-submit
          class="input h-8 w-full !rounded text-sm sm:!w-56"
          placeholder="Filter by part number"
          autocomplete="off"
          aria-label="Filter lines"
        />
        <button v-if="canTickAll" type="button" class="text-sm font-semibold text-blue-700 hover:underline" @click="tickAll">
          {{ query.trim() ? 'Tick shown lines' : 'Tick all lines left' }}
        </button>
        <button v-if="modelValue.length > 0" type="button" class="text-sm font-semibold text-blue-700 hover:underline" @click="emit('update:modelValue', [])">Clear</button>
      </span>
    </div>

    <!-- About 8 rows tall with its own scroll, so the total and the form's actions stay in view.
         scroll-pt-10 clears the sticky header (about 37px), so a focused quantity isn't hidden under it. -->
    <div class="overflow-hidden rounded border bg-white" :class="errorField === 'lines' ? 'border-red-400' : 'border-gray-200'">
      <div class="max-h-[26rem] scroll-pt-10 overflow-y-auto overscroll-contain">
        <div :class="row" class="sticky top-0 z-10 border-b border-gray-200 bg-white text-xs font-semibold uppercase tracking-wider text-gray-500">
          <span></span>
          <span>Line</span>
          <span>Part</span>
          <span class="text-right">Ordered</span>
          <span class="text-right">To plan</span>
          <span class="text-right">{{ compact ? 'Qty' : 'On this shipment' }}</span>
        </div>
        <label
          v-for="(l, i) in shown"
          :key="l.odooLineId"
          :class="[row, drafts.has(l.odooLineId) ? 'bg-blue-50/50' : '', i > 0 ? 'border-t border-gray-100' : '']"
          class="cursor-pointer"
        >
          <input
            type="checkbox"
            :checked="drafts.has(l.odooLineId)"
            :aria-label="`Line ${l.lineNo} ${l.partNumber ?? ''}`"
            :aria-describedby="errorField === 'lines' ? errorId : undefined"
            @change="toggle(l)"
          />
          <span class="tabular-nums text-gray-500">{{ l.lineNo }}</span>
          <span class="flex min-w-0 flex-col">
            <span class="break-all font-mono text-gray-900">{{ l.partNumber ?? '—' }}</span>
            <span class="break-words text-xs text-gray-500">{{ l.description }}</span>
          </span>
          <span class="text-right tabular-nums">{{ fmtPcs(l.ordered) }}</span>
          <span class="text-right tabular-nums">{{ fmtPcs(left.get(l.odooLineId) ?? 0) }}</span>
          <span class="text-right">
            <input
              v-if="drafts.has(l.odooLineId)"
              :value="drafts.get(l.odooLineId)?.qty"
              class="input h-8 !rounded text-right tabular-nums"
              :class="[compact ? 'w-20 px-2' : 'w-24', errorField === `qty-${l.odooLineId}` ? 'border-red-400' : '']"
              :data-field="`qty-${l.odooLineId}`"
              inputmode="numeric"
              :aria-label="`Pieces of line ${l.lineNo}`"
              :aria-invalid="errorField === `qty-${l.odooLineId}`"
              :aria-describedby="errorField === `qty-${l.odooLineId}` ? errorId : undefined"
              @input="setQty(l.odooLineId, ($event.target as HTMLInputElement).value)"
            />
            <span v-else class="text-gray-500">—</span>
          </span>
        </label>
        <label
          v-for="(d, j) in orphans"
          :key="`orphan-${d.odooLineId}`"
          :class="[row, shown.length + j > 0 ? 'border-t border-gray-100' : '']"
          class="cursor-pointer bg-amber-50/50"
        >
          <input type="checkbox" checked :aria-label="`Line ${d.lineNo}`" @change="remove(d.odooLineId)" />
          <span class="tabular-nums text-gray-500">{{ d.lineNo }}</span>
          <span class="text-xs font-semibold text-amber-800">No longer on the PO</span>
          <span></span>
          <span></span>
          <span class="text-right tabular-nums">{{ d.qty }}</span>
        </label>
        <p v-if="shown.length === 0 && orphans.length === 0" class="m-0 px-3 py-3 text-xs text-gray-500">
          {{ query.trim() ? `No line matches “${query.trim()}”.` : 'Every line already has a ship date.' }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
      <span class="tabular-nums">{{ draftsTotalText(modelValue) }}</span>
      <span v-if="hiddenCount > 0">· {{ linesLabel(hiddenCount) }} already fully planned or shipped</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { useElementWidth } from '@/composables/useElementWidth';
  import type { PoLineQuantities } from '@/types/poTracker';
  import { draftsTotalText, fmtPcs, lineMatches, linesLabel, tickAllLeft, type LineDraft } from '@/utils/poTracker';

  const LINE_FILTER_MIN = 8;
  const ROW = 'grid grid-cols-[28px_48px_minmax(0,1fr)_88px_100px_128px] items-center gap-3 px-3 py-2 text-sm';
  // A plan form in a narrow expanded PO: shrink the number columns so Part keeps the width.
  const COMPACT_ROW = 'grid grid-cols-[16px_32px_minmax(0,1fr)_56px_56px_80px] items-center gap-2 px-2 py-2 text-sm';
  const COMPACT_BELOW = 640;

  // errorField is `lines` (the whole table) or `qty-<odooLineId>` (one quantity).
  // errorId is the id of the message shown for errorField, linked from the field it marks.
  const props = withDefaults(defineProps<{ lines: PoLineQuantities[]; modelValue: LineDraft[]; left: Map<number, number>; errorField: string | null; errorId?: string }>(), {
    errorId: undefined,
  });
  const emit = defineEmits<{ 'update:modelValue': [value: LineDraft[]] }>();

  const query = ref('');
  const root = ref<HTMLElement | null>(null);
  const width = useElementWidth(root);
  const compact = computed(() => width.value > 0 && width.value < COMPACT_BELOW);
  const row = computed(() => (compact.value ? COMPACT_ROW : ROW));

  const drafts = computed(() => new Map(props.modelValue.map((d) => [d.odooLineId, d])));
  // Lines with nothing left stay hidden unless this shipment already carries them.
  const open = computed(() => props.lines.filter((l) => (props.left.get(l.odooLineId) ?? 0) > 0 || drafts.value.has(l.odooLineId)));
  const shown = computed(() => open.value.filter((l) => lineMatches(l, query.value)));
  const hiddenCount = computed(() => props.lines.length - open.value.length);
  const orphans = computed(() => props.modelValue.filter((d) => !props.lines.some((l) => l.odooLineId === d.odooLineId)));
  const canTickAll = computed(() => shown.value.some((l) => (props.left.get(l.odooLineId) ?? 0) > 0));

  function update(next: LineDraft[]): void {
    emit(
      'update:modelValue',
      [...next].sort((a, b) => a.lineNo - b.lineNo)
    );
  }

  function remove(odooLineId: number): void {
    update(props.modelValue.filter((d) => d.odooLineId !== odooLineId));
  }

  function toggle(l: PoLineQuantities): void {
    if (drafts.value.has(l.odooLineId)) {
      remove(l.odooLineId);
      return;
    }
    const left = props.left.get(l.odooLineId) ?? 0;
    update([...props.modelValue, { odooLineId: l.odooLineId, lineNo: l.lineNo, qty: left > 0 ? String(left) : '' }]);
  }

  function setQty(odooLineId: number, qty: string): void {
    update(props.modelValue.map((d) => (d.odooLineId === odooLineId ? { ...d, qty } : d)));
  }

  function tickAll(): void {
    update(tickAllLeft(shown.value, props.left, props.modelValue));
  }
</script>
